/* global dataLayer, window.dataLayer */
/* exported window.dataLayer */

import { useState, useEffect } from 'react';
import ProviderStep from '../providerStep/providerStep';
import ProgressBar from '../../components/progressBar/ProgressBar';
import ServicesStep from '../servicesStep/servicesStep';
import ServiceLocationStep from '../serviceLocationStep/serviceLocationStep';
import FundingStep from '../fundingStep/fundingStep';
import GovEntityStep from '../govEntityStep/govEntityStep';
import ResultsStep from '../resultsStep/resultsStep';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './quizController.module.css';
import DescriptionStep from '../descriptionStep/descriptionStep';
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs';
import ColorButton from '../../components/colorButton/colorButton';
import UnderlineButton from '../../components/underlineButton/underlineButton';
import BackIcon from '../../../img/icons/back.svg';
import RestartIcon from '../../../img/icons/restart.svg';
import ArrowLight from '../../../img/icons/arrow-light.svg';

const analyticsTrackResult = (formData) => {
  window.dataLayer = window.dataLayer || [];

  // prospective | existing | other
  const formUserType = formData.providerStep?.provider;

  // Service locations
  const locations = new Map(
    formData.locationStep?.locations?.map((item) => [
      item.value,
      item.isChecked
    ])
  );

  dataLayer.push({
    event: 'btnViewResults',
    userType: formUserType,
    deliverAct: locations.get('ACT'),
    deliverNsw: locations.get('NSW'),
    deliverNt: locations.get('NT'),
    deliverQld: locations.get('QLD'),
    deliverSa: locations.get('SA'),
    deliverTas: locations.get('TAS'),
    deliverVic: locations.get('VIC'),
    deliverWa: locations.get('WA'),
    deliverOther: locations.get('Other')
  });
};

export default function QuizController () {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [isReset, setIsReset] = useState(false);
  const [showAssociatedModal, setShowAssociatedModal] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      const currentParams = new URLSearchParams(window.location.search);
      const stepParam = currentParams.get('step');

      // Only intercept if step parameter exists
      if (stepParam !== null) {
        const newStep = parseInt(stepParam);
        setStep(newStep);
      }
    // If no step param, do nothing - let browser handle normally
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const descriptionOptions = [
    {
      label: 'Associated provider',
      value: 'ap',
      isChecked: false
    },
    {
      label: 'Aged care worker',
      value: 'acw',
      isChecked: false
    },
    {
      label: 'Digital platform operator',
      value: 'dpo',
      isChecked: false
    },
    {
      label: 'Responsible person',
      value: 'rp',
      isChecked: false
    }
  ];

  const serviceLocationOptions = [
    {
      label: 'Australian Capital Territory (ACT)',
      value: 'ACT',
      isChecked: false
    },
    {
      label: 'New South Wales (NSW)',
      value: 'NSW',
      isChecked: false
    },
    {
      label: 'Northern Territory (NT)',
      value: 'NT',
      isChecked: false
    },
    {
      label: 'Queensland (QLD)',
      value: 'QLD',
      isChecked: false
    },
    {
      label: 'South Australia (SA)',
      value: 'SA',
      isChecked: false
    },
    {
      label: 'Tasmania (TAS)',
      value: 'TAS',
      isChecked: false
    },
    {
      label: 'Victoria (VIC)',
      value: 'VIC',
      isChecked: false
    },
    {
      label: 'Western Australia (WA)',
      value: 'WA',
      isChecked: false
    },
    {
      label: 'Other Australian Territory',
      value: 'Other',
      isChecked: false
    }
  ];

  const fundingSourceOptions = [
    {
      label: 'Residential aged care',
      labelShort: 'Residential aged care',
      value: 'residential',
      isChecked: false
    },
    {
      label: 'Support at Home',
      labelShort: 'Support at Home',
      value: 'support',
      isChecked: false
    },
    {
      label: 'Commonwealth Home Support Program (CHSP)',
      labelShort: 'CHSP',
      value: 'CHSP',
      isChecked: false
    },
    {
      label:
        'National Aboriginal and Torres Strait Islander Flexible Aged Care Program (NATSIFACP)',
      labelShort: 'NATSIFACP',
      value: 'NATSIFACP',
      isChecked: false
    },
    {
      label: 'Multi-Purpose Service Program (MPSP)',
      labelShort: 'MPSP',
      value: 'MPSP',
      isChecked: false
    },
    {
      label: 'Transition Care Program (TCP)',
      labelShort: 'TCP',
      value: 'TCP',
      isChecked: false
    }
  ];

  const serviceCategories = [
    {
      id: 'CAT1',
      label: 'Category 1 - Home and community services',
      isSelected: false,
      subCategories: [
        { id: 'DA', label: 'Domestic assistance', isSelected: false },
        { id: 'HMR', label: 'Home maintenance and repairs', isSelected: false },
        { id: 'MEALS', label: 'Meals', isSelected: false },
        { id: 'TRANSPORT', label: 'Transport', isSelected: false }
      ],
      isSelectAllOnly: false,
      selectSubCategories: 0 // TODO - update this to be 'selectedSubCategories'
    },
    {
      id: 'CAT2',
      label: 'Category 2  - Assistive technology and home modifications',
      isSelected: false,
      subCategories: [
        { id: 'EP', label: 'Equipment and products', isSelected: false },
        { id: 'HA', label: 'Home adjustments', isSelected: false }
      ],
      isSelectAllOnly: false,
      selectSubCategories: 0
    },
    {
      id: 'CAT3',
      label: 'Category 3 - Advisory and support services',
      isSelected: false,
      subCategories: [
        {
          id: 'HSA',
          label: 'Hoarding and squalor assistance',
          isSelected: false
        },
        {
          id: 'SSCE',
          label: 'Social support and community engagement',
          isSelected: false
        }
      ],
      isSelectAllOnly: false,
      selectSubCategories: 0
    },
    {
      id: 'CAT4',
      label:
        'Category 4 - Personal care and care support in the home or community',
      isSelected: false,
      subCategories: [
        { id: 'AHT', label: 'Allied health and therapy', isSelected: false },
        { id: 'PC', label: 'Personal care', isSelected: false },
        { id: 'Nutrition', label: 'Nutrition', isSelected: false },
        {
          id: 'TSIL',
          label: 'Therapeutic services for independent living',
          isSelected: false
        },
        {
          id: 'HCGR',
          label: 'Home or community general respite',
          isSelected: false
        },
        { id: 'CCR', label: 'Community cottage respite', isSelected: false },
        { id: 'CM', label: 'Care management', isSelected: false },
        { id: 'RCM', label: 'Restorative care management', isSelected: false }
      ],
      isSelectAllOnly: false,
      selectSubCategories: 0
    },
    {
      id: 'CAT5',
      label: 'Category 5 - Nursing and transition care',
      isSelected: false,
      subCategories: [
        { id: 'NC', label: 'Nursing care', isSelected: false },
        {
          id: 'ATC',
          label: 'Assistance with transition care',
          isSelected: false
        }
      ],
      isSelectAllOnly: false,
      selectSubCategories: 0
    },
    {
      id: 'CAT6',
      label: 'Category 6 - Residential care',
      isSelected: false,
      subCategories: [
        { id: 'RA', label: 'Residential accommodation', isSelected: false },
        { id: 'RCC', label: 'Residential clinical care', isSelected: false },
        {
          id: 'RNCC',
          label: 'Residential non-clinical care',
          isSelected: false
        },
        { id: 'REL', label: 'Residential everyday living', isSelected: false }
      ],
      isSelectAllOnly: true,
      selectSubCategories: 0
    }
  ];

  const govEntityOptions = [
    {
      label: 'Yes',
      value: true,
      isChecked: false
    },
    {
      label: 'No',
      value: false,
      isChecked: false
    }
  ];

  const defaultFormState = {
    providerStep: {
      provider: '', // Valid options are "registered", "prospective" or "other"
      isValid: false
    },
    descriptionStep: {
      value: '',
      description: descriptionOptions,
      isValid: false
    },
    locationStep: {
      locations: serviceLocationOptions,
      isValid: false
    },
    servicesStep: {
      serviceCategories,
      isValid: false
    },
    fundingStep: {
      fundingSource: fundingSourceOptions,
      isValid: false
    },
    govEntityStep: {
      isGovEntity: govEntityOptions,
      isValid: false
    },
    resultsStep: {
      topic: 'all'
    }
  };

  const [formData, setFormData] = useState(defaultFormState);

  const resetForm = () => {
    setFormData(defaultFormState);
    setIsReset(true);
    setStep(0);
  };

  const handleStepSkip = () => {
    // Update form state
    const currentStep = otherQuizSteps[step].id;
    const currentStepDefault = defaultFormState[currentStep];
    setFormData((prevData) => ({
      ...prevData,
      [currentStep]: currentStepDefault
    }));

    // Update url params
    let urlParamKey = ' ';
    urlParamKey = otherQuizSteps[step].id[0];
    const searchParams = new URLSearchParams(window.location.search);
    const paramExists = searchParams.get(urlParamKey);
    if (paramExists != undefined) {
      searchParams.delete(urlParamKey);
    }
    // TODO: check if this needs to change
    setStep(step + 1);
  };

  const quizSteps = [
    {
      id: 'providerStep',
      component: <ProviderStep formData={formData} setFormData={setFormData} defaultState={defaultFormState} />
    },
    {
      // id: 'locationStep',
      id: 'locationStep',
      component: (
        <ServiceLocationStep formData={formData} setFormData={setFormData} />
      )
    },
    {
      id: 'servicesStep',
      component: <ServicesStep formData={formData} setFormData={setFormData} />
    },
    {
      id: 'fundingStep',
      component: <FundingStep formData={formData} setFormData={setFormData} />
    },
    {
      id: 'govEntityStep',
      component: (
        <GovEntityStep formData={formData} setFormData={setFormData} />
      )
    },
    {
      id: 'resultStep',
      component: (
        <ResultsStep
          formData={formData}
          setFormData={setFormData}
          handleEditDetails={() => {
            setStep(0);
          }}
        />
      )
    }
  ];

  const otherQuizSteps = [
    {
      id: 'providerStep',
      component: <ProviderStep formData={formData} setFormData={setFormData} defaultState={defaultFormState} />
    },
    {
      id: 'descriptionStep',
      component: (
        <DescriptionStep formData={formData} setFormData={setFormData} />
      )
    },
    {
      id: 'servicesStep',
      component: <ServicesStep formData={formData} setFormData={setFormData} />
    },
    {
      id: 'fundingStep',
      component: <FundingStep formData={formData} setFormData={setFormData} />
    },
    {
      id: 'govEntityStep',
      component: (
        <GovEntityStep formData={formData} setFormData={setFormData} />
      )
    },
    {
      id: 'resultStep',
      component: (
        <ResultsStep
          formData={formData}
          setFormData={setFormData}
          handleEditDetails={() => {
            setStep(0);
          }}
        />
      )
    }
  ];

  // Set form values on page load functions
  const updateStep = () => {
    const param = searchParams.get('step');
    if (param != undefined) {
      setStep(parseInt(searchParams.get('step')));
    }
  };

  const updateProvider = () => {
    const param = searchParams.get('p');
    if (param != undefined) {
      setFormData((prevData) => ({
        ...prevData,
        providerStep: { provider: param.toString(), isValid: true }
      }));
    }
  };

  const updateDescription = () => {
    const param = searchParams.get('d');
    if (param != undefined) {
      let updatedDescription = formData.descriptionStep.description;
      updatedDescription = updatedDescription.map((item) =>
        item.value === param.toString() ? { ...item, isChecked: true } : item
      );
      const selection = updatedDescription.find((item) => item.isChecked);

      setFormData((prevData) => ({
        ...prevData,
        descriptionStep: {
          value: selection?.value,
          description: updatedDescription,
          isValid: true
        }
      }));
    }
  };

  const updateLocation = () => {
    const param = searchParams.get('l');
    if (param != undefined) {
      let updatedLocation = formData.locationStep.locations;

      const locationParams = param.toString().split(',');
      locationParams.forEach((param) => {
        updatedLocation = updatedLocation.map((item) =>
          item.value === param.toString() ? { ...item, isChecked: true } : item
        );
      });

      setFormData((prevData) => ({
        ...prevData,
        locationStep: {
          locations: updatedLocation,
          isValid: true
        }
      }));
    }
  };

  const updateServices = () => {
    const param = searchParams.get('s');
    if (param != undefined) {
      const updatedServices = formData.servicesStep.serviceCategories;
      const paramList = param.toString().split(',');
      paramList.forEach((item) => {
        // If all or if category 6
        if (item[1] === 'A' || parseInt(item[1] == 5)) {
          updatedServices[parseInt(item[0])].selectSubCategories =
            updatedServices[parseInt(item[0])].subCategories.length;
          updatedServices[parseInt(item[0])].isSelected = true;
          updatedServices[parseInt(item[0])].subCategories.forEach(
            (sub) => (sub.isSelected = true)
          );
        } else {
          updatedServices[parseInt(item[0])].subCategories[
            item[1]
          ].isSelected = true;
          updatedServices[parseInt(item[0])].selectSubCategories += 1;
        }
      });

      setFormData((prevData) => ({
        ...prevData,
        servicesStep: {
          serviceCategories: updatedServices,
          isValid: true
        }
      }));
    }
  };

  const updateFunding = () => {
    const param = searchParams.get('f');
    if (param != undefined) {
      let updatedFunding = formData.fundingStep.fundingSource;
      const fundingParams = param.toString().split(',');
      fundingParams.forEach((param) => {
        updatedFunding = updatedFunding.map((item) =>
          item.value === param.toString() ? { ...item, isChecked: true } : item
        );
      });

      setFormData((prevData) => ({
        ...prevData,
        fundingStep: {
          fundingSource: updatedFunding,
          isValid: true
        }
      }));
    }
  };

  const updateGovEntity = () => {
    const param = searchParams.get('g');
    if (param != undefined) {
      let updatedGovEntity = formData.govEntityStep.isGovEntity;
      updatedGovEntity = updatedGovEntity.map((item) =>
        item.value.toString() === param.toString()
          ? { ...item, isChecked: true }
          : item
      );

      setFormData((prevData) => ({
        ...prevData,
        govEntityStep: {
          isGovEntity: updatedGovEntity,
          isValid: true
        }
      }));
    }
  };

  const updateResults = () => {
    const param = searchParams.get('r');
    if (param != undefined) {
      setFormData((prevData) => ({
        ...prevData,
        resultsStep: {
          topic: param.toString()
        }
      }));
    }
  };

  const readUrlParams = () => {
    updateStep();
    updateProvider();
    updateDescription();
    updateLocation();
    updateServices();
    updateFunding();
    updateGovEntity();
    updateResults();
  };

  // TODO - remove all console.logs from project
  useEffect(() => {
    readUrlParams();
  }, []);

  useEffect(() => {
    let searchParams;
    if (isReset) {
      searchParams = new URLSearchParams();
    } else {
      searchParams = new URLSearchParams(window.location.search);
    }
    searchParams.set('step', step);

    const newUrl = `?${searchParams.toString()}`;
    navigate(newUrl, { replace: true });
    setIsReset(false);
  }, [step]);

  useEffect(() => {
    // Force scroll after React has finished rendering (for some reason, this was the only way to allow a smooth scroll for every step)
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 0);
  }, [step]);

  const displayNavBar = () => {
    const isOther = formData.providerStep.provider === 'other';
    const showSkip =
      formData.providerStep.provider === 'other' &&
      step > 0 &&
      otherQuizSteps[step].id !== 'servicesStep' &&
      !formData.descriptionStep.description[0].isChecked;

    const isLastQuestion =
      (formData.providerStep.provider === 'other' &&
        (step === otherQuizSteps.length - 2 ||
          (step === 1 &&
            (formData.descriptionStep.description[1].isChecked ||
              formData.descriptionStep.description[2].isChecked ||
              formData.descriptionStep.description[3].isChecked)))) ||
      (formData.providerStep.provider !== 'other' &&
        step === quizSteps.length - 2);

    const setNextButtonDisabled = () => {
      if (
        formData.providerStep.provider === 'other' &&
        !formData[otherQuizSteps[step].id].isValid
      ) {
        return true;
      }

      if (
        formData.providerStep.provider !== 'other' &&
        !formData[quizSteps[step].id].isValid
      ) {
        return true;
      }
      return false;
    };

    const setSkipButtonDisabled = () => {
      // Check if there is a 'd' parameter in the search params
      const searchParams = new URLSearchParams(window.location.search);
      const dParam = searchParams.get('d');

      // Return true (disabled) if 'd' parameter exists, false otherwise
      return dParam !== null && dParam !== undefined && dParam !== '';
    };

    if (
      (isOther && otherQuizSteps[step].id === 'resultStep') ||
      quizSteps[step].id === 'resultStep'
    ) {
      return null;
    }

    return (
      <div className={styles.navigationBar}>
        {step > 0 && (
          <UnderlineButton
            label='Previous'
            onClick={() => {
              // Get current search params
              const searchParams = new URLSearchParams(window.location.search);

              // Update the step parameter (increment by 1)
              const currentStep = parseInt(searchParams.get('step') || '0', 10);
              // Navigate with updated search params
              searchParams.set('step', (currentStep - 1).toString());

              // Navigate with updated search params
              navigate(`?${searchParams.toString()}`);
              setStep(step - 1);
            }}
            iconLeft={<BackIcon className={styles.prevIcon} />}
          />
        )}
        <div className={styles.groupRight}>
          {step > 0 && (
            <UnderlineButton
              label='Start again'
              onClick={() => {
                resetForm();
                setStep(0);
              }}
              iconRight={<RestartIcon className={styles.restartIcon} />}
              isHiddenMobile
            />
          )}
          {showSkip && (
            <UnderlineButton isDisabled={setSkipButtonDisabled()} label='Skip' onClick={handleStepSkip} />
          )}
          <ColorButton
            label={isLastQuestion ? 'View results' : 'Next'}
            onClick={() => {
              // Get current search params
              const searchParams = new URLSearchParams(window.location.search);

              // Update the step parameter (increment by 1)
              const currentStep = parseInt(searchParams.get('step') || '0', 10);
              if (isLastQuestion) {
                searchParams.set('step', (otherQuizSteps.length - 1).toString());
                // Navigate with updated search params
                navigate(`?${searchParams.toString()}`);
                setStep(otherQuizSteps.length - 1);
                analyticsTrackResult(formData);
              } else if (isOther && step === 1) {
                setShowAssociatedModal(true);
              } else {
                searchParams.set('step', (currentStep + 1).toString());

                // Navigate with updated search params
                navigate(`?${searchParams.toString()}`);
                setStep(step + 1);
              }
            }}
            isDisabled={setNextButtonDisabled()}
          />
        </div>
      </div>
    );
  };

  const isResultsStep =
    (formData.providerStep.provider === 'other' &&
      otherQuizSteps[step].id === 'resultStep') ||
    (formData.providerStep.provider !== 'other' &&
      quizSteps[step].id === 'resultStep');

  return (
    <>
      {showAssociatedModal && (
        <div className={styles.associatedModalBackground}>
          <div className={styles.associatedModalContainer}>
            <p className={styles.associatedModalTitle}>
              Information for Associated Providers
            </p>
            <p className={styles.associatedModalText}>
              Associated provider is a new concept introduced by the Act. Registered providers 
              are responsible for ensuring associated providers comply with relevant requirements. To learn more
              about associated providers and how they operate under the new Act,
              please view the information provided on{' '}
              <a href='https://www.health.gov.au/topics/aged-care/providing-aged-care-services/associated-providers'>
                Associated providers | Australian Government Department of Health, Disability and Ageing
              </a>
              .
            </p>
            <div className={styles.associatedModalButtonContainer}>
              <UnderlineButton
                label='Go back'
                onClick={() => setShowAssociatedModal(false)}
              />
              <ColorButton
                label='Visit health.gov.au'
                iconRight={<ArrowLight className={styles.arrowIcon} />}
                onClick={() =>
                  window.open(
                    'https://www.health.gov.au/resources/videos/associated-providers-under-the-new-aged-care-act-new-regulatory-model?language=en',
                    '_blank'
                  )}
              />
            </div>
          </div>
        </div>
      )}
      <Breadcrumbs />
      <div className='container'>
        <ProgressBar
          currentStep={step}
          totalSteps={
            formData.providerStep.provider === 'other'
              ? otherQuizSteps.length
              : quizSteps.length
          }
        />
        <form
          className={`au-body ${styles.formContainer} ${
            isResultsStep ? styles.formContainerResultsStep : ''
          }`}
        >
          {formData.providerStep.provider === 'other'
            ? otherQuizSteps[step].component
            : quizSteps[step].component}
          {displayNavBar()}{' '}
        </form>
      </div>
    </>
  );
}
